import { useEffect, useState } from "react";

export type FeedbackRating = "resolved" | "unresolved" | "doubt";

const FEEDBACK_OPTIONS: Array<{ value: FeedbackRating; label: string }> = [
	{ value: "resolved", label: "解決" },
	{ value: "unresolved", label: "未解決" },
	{ value: "doubt", label: "疑義あり" },
];

interface FeedbackBarProps {
	onSubmit?: (rating: FeedbackRating, comment: string) => void;
	disabled?: boolean;
	answerKey: string;
}

export function FeedbackBar({ onSubmit, disabled, answerKey }: FeedbackBarProps) {
	const [rating, setRating] = useState<FeedbackRating | null>(null);
	const [status, setStatus] = useState<"idle" | "submitted" | "needsRating">("idle");

	useEffect(() => {
		setRating(null);
		setStatus("idle");
	}, [answerKey]);

	const handleSubmit = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!rating) {
			setStatus("needsRating");
			return;
		}
		onSubmit?.(rating, "");
		setStatus("submitted");
	};

	return (
		<section style={{ borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: 'rgba(248, 250, 252, 0.8)', padding: '12px' }}>
			<div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
				{/* Top: Title + Explanation */}
				<div style={{ textAlign: 'center' }}>
					<p style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', margin: 0 }}>回答のフィードバック</p>
					<p style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', marginBottom: 0 }}>
						ご評価をお選びいただき、「送信」ボタンを押すと反映されます
					</p>
				</div>

				{/* Middle: Rating buttons */}
				<div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
					{FEEDBACK_OPTIONS.map((option) => (
						<button
							key={option.value}
							type="button"
							disabled={disabled}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setRating(option.value);
								setStatus("idle");
							}}
							style={{
								margin: 0,
								padding: '6px 12px',
								fontSize: '13px',
								fontWeight: 500,
								borderRadius: '6px',
								border: '1px solid',
								borderColor: rating === option.value ? '#059669' : '#cbd5e1',
								backgroundColor: rating === option.value ? '#059669' : '#ffffff',
								color: rating === option.value ? '#ffffff' : '#334155',
								cursor: disabled ? 'not-allowed' : 'pointer',
								opacity: disabled ? 0.5 : 1,
								transition: 'all 0.15s',
							}}
						>
							{option.label}
						</button>
					))}
				</div>

				{/* Bottom: Submit button */}
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<button
						type="button"
						disabled={disabled}
						onClick={handleSubmit}
						style={{
							margin: 0,
							padding: '6px 16px',
							fontSize: '13px',
							fontWeight: 500,
							borderRadius: '6px',
							border: 'none',
							backgroundColor: status === "submitted" ? '#6b7280' : '#2563eb',
							color: '#ffffff',
							cursor: disabled ? 'not-allowed' : 'pointer',
							opacity: disabled ? 0.5 : 1,
							transition: 'all 0.15s',
						}}
					>
						{status === "submitted" ? "送信済み" : "送信"}
					</button>
				</div>

				{/* Status messages */}
				{status === "submitted" && (
					<p style={{ textAlign: 'center', fontSize: '11px', color: '#059669', margin: 0 }}>フィードバックを受け付けました。ありがとうございます。</p>
				)}
				{status === "needsRating" && (
					<p style={{ textAlign: 'center', fontSize: '11px', color: '#f43f5e', margin: 0 }}>評価を選択してから送信してください。</p>
				)}
			</div>
		</section>
	);
}
